import { Box, Button, Stack } from '@mui/material';
import { ConfirmationDialog, PoolFormattedMessage, UrlFilter } from '@synergy/components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    PaloColumn,
    PaloFilterChips,
    PaloFilterModal,
    PaloListView,
    PaloRolePills,
    PaloRowAction,
    PaloStatusPill,
} from '@synergy/components-palo';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import * as UserManagementActions from '../actions/user-management-actions';
import { Employee } from '../models/employee';
import { UserManagementDeleteModal } from '../user-management-delete-modal';
import { UserManagementDetailModal } from '../user-management-detail-modal';
import { UserStatusFilter } from '../../../common/components/filter/employee-filter';
import { getUserStatus } from '../../../common/utils/get-contact-type';
import { getUserStatusTranslationId } from '../../../common/utils/get-user-status-translation-id';
import { updateAccountLastChecked } from '../../../common/actions/current-account-actions';
import { updateMainContact } from '../actions/user-management-actions';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** As the legacy filter, without its search field — the list header owns that. */
const PaloUserManagementFilter = (): ReactElement => (
    <PaloFilterModal>
        <UserStatusFilter />
    </PaloFilterModal>
);

export const PaloUserManagementOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const employeeData = useSelector(state => state.userManagement.employeeData);
    const accountUserNeedsToBeChecked = useSelector(state => state.currentAccount.accountNeedsToBeChecked);
    const user = useSelector(state => state.user);

    const [showUpsertModal, setShowUpsertModal] = useState(false);
    const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
    const [isDeleteEmployeeModalOpen, setIsDeleteEmployeeModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<Employee>();

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const sortKey = searchParams.get('sort') ?? 'lastName';
    const sortOrder = searchParams.get('order') ?? 'asc';
    /*
     * The main contact stands at the top — but only while the list is in the
     * order it opens in. Once the user sorts it themselves, theirs is the only
     * order there is (LST-S3a as amended on 12.10.2026).
     *
     * One page at a time is enough: of 3.816 accounts exactly one has more
     * than 50 people on it (measured 13.10.2026).
     */
    const defaultSorted = 'lastName' === sortKey && 'asc' === sortOrder;
    const items = defaultSorted
        ? [...employeeData.items].sort((a, b) => Number(b.isMainContact) - Number(a.isMainContact))
        : employeeData.items;

    useEffect(() => {
        dispatch(UserManagementActions.fetchEmployees(searchParams));

        return () => {
            dispatch(UserManagementActions.resetUser());
        };
    }, [dispatch, searchParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openModal = (employee?: Employee): void => {
        setSelectedUser(employee);
        setShowUpsertModal(true);
    };

    const confirmDeleteEmployee = (
        employee: Employee,
        reassignMainContactId?: number,
        reassignBonusContactId?: number,
        reassignImportContactId?: number
    ): void => {
        setIsDeleteEmployeeModalOpen(false);
        setShowUpsertModal(false);

        if (selectedUser?.id) {
            dispatch(
                UserManagementActions.deleteEmployee(
                    employee.id,
                    reassignMainContactId,
                    reassignBonusContactId,
                    reassignImportContactId
                )
            );
        }
    };

    const confirmDeleteUser = (): void => {
        setIsDeleteUserModalOpen(false);
        setShowUpsertModal(false);

        if (selectedUser?.id) {
            dispatch(UserManagementActions.deleteLoginUser(selectedUser.id));
        }
    };

    /** What this person is here, as pills behind the name (LST-S3a). */
    const roles = (employee: Employee): ReactNode[] => [
        employee.isMainContact && <FormattedMessage id="app.user.management.delete.mainContact" />,
        employee.hasBonusConditions && <FormattedMessage id="app.user.management.delete.bonusContact" />,
        employee.isImportContact && <FormattedMessage id="app.user.management.delete.importContact" />,
    ];

    const columns: PaloColumn<Employee>[] = [
        {
            key: 'lastName',
            type: 'name',
            head: <FormattedMessage id="app.table.column.lastName" />,
            sortKey: 'lastName',
            cell: employee => (
                <Stack direction="row" gap={1} alignItems="center">
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{employee.lastName}</Box>
                    <PaloRolePills roles={roles(employee)} />
                </Stack>
            ),
        },
        {
            key: 'firstName',
            type: 'name',
            head: <FormattedMessage id="app.table.column.firstName" />,
            sortKey: 'firstName',
            cell: employee => employee.firstName,
        },
        {
            key: 'email',
            type: 'name',
            head: <FormattedMessage id="app.table.column.email" />,
            sortKey: 'email',
            variant: 'muted',
            cell: employee => employee.email,
        },
        {
            key: 'userStatus',
            type: 'status',
            head: <FormattedMessage id="app.user.status.userStatus" />,
            cell: employee => {
                const status = getUserStatus(employee);

                return (
                    <PaloStatusPill
                        status={status}
                        label={<FormattedMessage id={getUserStatusTranslationId(status)} />}
                    />
                );
            },
        },
    ];

    const rowActions = (employee: Employee): PaloRowAction[] => {
        const actions: PaloRowAction[] = [];
        const isSelf = user.contact.id === employee.id;

        if (!employee.isMainContact) {
            actions.push({
                label: <FormattedMessage id="app.user.management.delete.mainContact" />,
                onClick: () => dispatch(updateMainContact(employee.id)),
            });
        }
        if (!isSelf) {
            actions.push({
                label: <FormattedMessage id="app.user.management.delete.employee" />,
                onClick: () => {
                    setSelectedUser(employee);
                    setIsDeleteEmployeeModalOpen(true);
                },
            });
        }
        if (!isSelf && employee.user) {
            actions.push({
                label: <FormattedMessage id="app.user.management.delete.user" />,
                onClick: () => {
                    setSelectedUser(employee);
                    setIsDeleteUserModalOpen(true);
                },
            });
        }

        return actions;
    };

    const headerActions = (
        <Stack direction="row" gap={1}>
            {accountUserNeedsToBeChecked && (
                <Button variant="outlined" color="primary" onClick={() => dispatch(updateAccountLastChecked())}>
                    <FormattedMessage id="app.user.management.dataCheck" />
                </Button>
            )}
            <Button variant="contained" color="primary" onClick={() => openModal()}>
                <FormattedMessage id="app.common.newUser" />
            </Button>
        </Stack>
    );

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<Employee>
                title={<FormattedMessage id="app.navigation.userManagement" />}
                headerActions={headerActions}
                filters={<PaloUserManagementFilter />}
                filterChips={<PaloFilterChips />}
                sort={{ key: sortKey, order: sortOrder as 'asc' | 'desc' }}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={items}
                getRowId={employee => employee.id}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                onRowClick={openModal}
                rowActions={rowActions}
                pagination={{
                    count: employeeData.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                <UserManagementDetailModal
                    visible={showUpsertModal}
                    user={selectedUser}
                    onClose={() => {
                        setSelectedUser(undefined);
                        setShowUpsertModal(false);
                    }}
                />
                <UserManagementDeleteModal
                    isOpen={isDeleteEmployeeModalOpen}
                    user={selectedUser}
                    onClose={() => setIsDeleteEmployeeModalOpen(false)}
                    onConfirm={confirmDeleteEmployee}
                />
                <ConfirmationDialog
                    title={<FormattedMessage id="app.user.management.delete.user" />}
                    action={<FormattedMessage id="app.common.delete" />}
                    isVisible={isDeleteUserModalOpen}
                    onCancel={() => setIsDeleteUserModalOpen(false)}
                    onConfirm={confirmDeleteUser}
                >
                    <PoolFormattedMessage
                        id="app.user.management.delete.user.message"
                        values={{ value: selectedUser?.fullName }}
                    />
                </ConfirmationDialog>
            </PaloListView>
        </UrlFilter>
    );
};
